import { FileText, Pencil, Trash2, Plus, Search, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { openDocument } from "@/lib/api";

export const CaseRecordsTable = ({ cases, search, setSearch, onNew, onEdit, onDelete }) => {
  return (
    <div className="overflow-hidden rounded-lg border border-white/10 bg-[#111723]">
      <div className="flex flex-col gap-3 border-b border-white/10 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-display text-lg font-bold text-slate-50">Case Records Database</h3>
          <p className="mt-0.5 text-xs text-slate-400">The data source searched when a lawyer sends a case number</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input
              data-testid="case-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search cases..."
              className="h-9 w-48 border-white/10 bg-[#0B0F17] pl-8 text-sm text-slate-200"
            />
          </div>
          <Button data-testid="new-case-button" onClick={onNew} className="h-9 bg-blue-600 text-white hover:bg-blue-700">
            <Plus className="mr-1 h-4 w-4" /> New Case
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3 font-medium">Case No.</th>
              <th className="px-4 py-3 font-medium">Title</th>
              <th className="px-4 py-3 font-medium">Parties</th>
              <th className="px-4 py-3 font-medium">Next Hearing</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Docs</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5" data-testid="cases-table-body">
            {cases.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No case records. Create one to get started.</td></tr>
            )}
            {cases.map((c) => (
              <tr key={c.id} data-testid={`case-row-${c.id}`} className="transition-colors duration-200 hover:bg-[#1A2234]">
                <td className="whitespace-nowrap px-4 py-3 font-mono-data text-xs font-semibold text-blue-400">{c.case_number}</td>
                <td className="px-4 py-3 text-slate-200">{c.title}</td>
                <td className="px-4 py-3 text-xs text-slate-400">
                  <div className="flex flex-wrap gap-1">
                    {(c.parties || []).map((p, i) => (
                      <span key={i} className="rounded bg-slate-800 px-1.5 py-0.5">{p}</span>
                    ))}
                  </div>
                </td>
                <td className="whitespace-nowrap px-4 py-3 font-mono-data text-xs text-slate-300">{c.next_hearing_date || "—"}</td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className="border-white/15 text-xs text-slate-300">{c.status}</Badge>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    {(c.documents || []).length > 0 ? (
                      (c.documents || []).map((d) => (
                        <button key={d.id} onClick={() => openDocument(c.id, d.id)} title={d.original_filename} data-testid={`case-doc-${d.id}`} className="text-slate-400 hover:text-blue-400">
                          <FileText className="h-4 w-4" />
                        </button>
                      ))
                    ) : (
                      <span className="text-xs text-slate-600">—</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button data-testid={`edit-case-${c.id}`} onClick={() => onEdit(c)} className="rounded p-1.5 text-slate-400 transition-colors duration-200 hover:bg-slate-800 hover:text-white">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button data-testid={`delete-case-${c.id}`} onClick={() => onDelete(c)} className="rounded p-1.5 text-slate-400 transition-colors duration-200 hover:bg-slate-800 hover:text-red-400">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
